/**
 * The focus timer's effects on the world outside React state: the tick that
 * applies due transitions, the tab title, and the announcements when a block
 * or a break ends. Split from the provider so each stays one concern.
 */
import { useEffect, useRef } from 'react';
import { notify } from '@/lib/notify';
import {
  elapsedMs,
  remainingMs,
  type FocusAction,
  type FocusPhase,
  type FocusState,
} from '@/lib/focus/focusMachine';
import {
  focusTitle,
  playChime,
  showBrowserNotification,
  type FocusSettings,
} from '@/lib/focus/focusAlerts';
import { formatMinutes } from '@/lib/focus/format';

/** Apply due transitions every second, and at once when the tab comes back. */
export function useFocusTicker(
  phase: FocusPhase,
  dispatch: (action: FocusAction) => void
): void {
  useEffect(() => {
    if (phase === 'idle') return;
    const tick = () => dispatch({ type: 'tick' });
    tick();
    const id = window.setInterval(tick, 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [phase, dispatch]);
}

const PHASE_LABEL = {
  focus: 'Focus',
  paused: 'Paused',
  break: 'Break',
} as const;

/**
 * `12:30 · Focus — Xonera` while a block or break runs. Written imperatively
 * on its own interval so the provider does not re-render every consumer once
 * a second just to move the title.
 */
export function useFocusTitle(state: FocusState, enabled: boolean): void {
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    if (!enabled || state.phase === 'idle') return;
    const base = document.title;
    const write = () => {
      const s = latest.current;
      if (s.phase === 'idle') return;
      const now = Date.now();
      const ms = remainingMs(s, now) ?? elapsedMs(s, now);
      document.title = focusTitle(base, PHASE_LABEL[s.phase], ms);
    };
    write();
    const id = window.setInterval(write, 1000);
    return () => {
      window.clearInterval(id);
      document.title = base;
    };
  }, [enabled, state.phase]);
}

function announceBlockEnd(
  state: FocusState,
  settings: FocusSettings,
  openBreakSheet: () => void
): void {
  const block = state.lastBlock;
  const minutes = block ? formatMinutes(block.focusMs) : '';
  const title = block?.endedEarly ? `${minutes} counts.` : 'Nice focus.';
  const description = 'Time for a breather.';
  notify.action(
    {
      title,
      description,
      actionLabel: 'Take your break',
      onAction: openBreakSheet,
    },
    { id: 'focus-block-ended' }
  );
  if (settings.sound) playChime();
  if (settings.notifications) {
    showBrowserNotification(`${title} ${description}`, `${minutes} of focus.`);
  }
}

/** The last planned round ended: the session is over, no break follows. */
function announceSessionEnd(state: FocusState, settings: FocusSettings): void {
  const rounds = state.lastSession?.blocks ?? 0;
  const description = `${rounds} ${rounds === 1 ? 'round' : 'rounds'} done. Your summary is on the Focus page.`;
  notify.success(`Session complete. ${description}`, {
    id: 'focus-session-ended',
    duration: 6000,
  });
  if (settings.sound) playChime();
  if (settings.notifications)
    showBrowserNotification('Session complete. Nice work.', description);
}

/**
 * Announce a block ending, a session ending, and a break ending - on the transition only.
 * The previous phase starts as the CURRENT one, so mounting onto a break
 * (a refresh, the second tab) announces nothing that already happened.
 */
export function useFocusTransitions(
  state: FocusState,
  settings: FocusSettings,
  openBreakSheet: () => void
): void {
  const previous = useRef<FocusPhase>(state.phase);

  useEffect(() => {
    const from = previous.current;
    previous.current = state.phase;
    if (from === state.phase) return;

    const wasFocusing = from === 'focus' || from === 'paused';
    if (wasFocusing && state.phase === 'break') {
      announceBlockEnd(state, settings, openBreakSheet);
    } else if (wasFocusing && state.phase === 'idle' && state.lastSession) {
      announceSessionEnd(state, settings);
    } else if (from === 'break' && state.phase === 'idle') {
      notify.dismiss('focus-block-ended');
      notify.info("Break's over. Ready when you are.", {
        id: 'focus-break-ended',
      });
      if (settings.sound) playChime();
    }
    // Keyed on the phase alone: the transition is the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);
}
